import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Button,
  Container,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  saveUnsavedChanges,
  selectAssessmentDetails,
  updateDetails,
} from '../../redux/reducers/assessmentReducer';

function ReviewDetails({ readOnly = false }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const assessmentDetails = useSelector(selectAssessmentDetails);
  const [editMode, setEditMode] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [textFields, setTextFields] = useState({
    auditees: assessmentDetails.auditees ?? '',
    auditors: assessmentDetails.auditors ?? '',
    scope: assessmentDetails.scope ?? '',
    criteria: assessmentDetails.criteria ?? '',
    objectives: assessmentDetails.objectives ?? '',
  });

  const handleExpanded = () => {
    setExpanded(!expanded);
  };

  const handleTextChange = (event) => {
    const { name, value } = event.target;
    const newState = { ...textFields };
    newState[name] = value;
    setTextFields(newState);
  };

  const handleSave = () => {
    const newDetails = {
      auditees: textFields.auditees,
      auditors: textFields.auditors,
      scope: textFields.scope,
      criteria: textFields.criteria,
      objectives: textFields.objectives,
    };
    dispatch(updateDetails(newDetails));
  };

  const toggleEditMode = () => {
    if (editMode) {
      dispatch(saveUnsavedChanges(''));
    }
    setEditMode(!editMode);
  };

  useEffect(() => () => {
    dispatch(saveUnsavedChanges(''));
  }, []);

  return (
    <Container disableGutters sx={{ px: 0.25 }}>
      <Paper elevation={4} sx={{ px: 2, py: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" onClick={() => handleExpanded()}>
          <Typography variant="h6">{t('assessment.details.title')}</Typography>
          <IconButton id='expand-details'>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Stack>
        {expanded && (
          <Container maxWidth="false" disableGutters sx={{ my: 2 }}>
            <Stack spacing={2}>
              <Divider />
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography>{t('assessment.details.info')}</Typography>
                {!readOnly && (
                  <Button
                    id='edit-details'
                    variant="outlined"
                    endIcon={editMode ? <CheckIcon /> : <EditIcon />}
                    onClick={toggleEditMode}
                  >
                    {editMode ? t('assessment.details.buttonCloseEdit') : t('assessment.details.buttonEdit')}
                  </Button>
                )}
              </Stack>
              <Stack spacing={editMode ? 3 : 2} sx={{ mt: 2 }}>
                {editMode ? (
                  <TextField
                    id="auditees"
                    name="auditees"
                    value={textFields.auditees}
                    onChange={handleTextChange}
                    onBlur={handleSave}
                    multiline
                    rows="4"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    label={t('assessment.details.auditees')}
                    sx={{ backgroundColor: 'white' }}
                  />
                ) : (
                  <Stack spacing={1}>
                    <Typography>{t('assessment.details.auditees')}</Typography>
                    <Typography variant="body2">{assessmentDetails.auditees}</Typography>
                  </Stack>
                )}
                {editMode ? (
                  <TextField
                    id="auditors"
                    name="auditors"
                    value={textFields.auditors}
                    onChange={handleTextChange}
                    onBlur={handleSave}
                    multiline
                    rows="4"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    label={t('assessment.details.auditors')}
                    sx={{ backgroundColor: 'white' }}
                  />
                ) : (
                  <Stack spacing={1}>
                    <Typography>{t('assessment.details.auditors')}</Typography>
                    <Typography variant="body2">{assessmentDetails.auditors}</Typography>
                  </Stack>
                )}
                {editMode ? (
                  <TextField
                    id="scope"
                    name="scope"
                    value={textFields.scope}
                    onChange={handleTextChange}
                    onBlur={handleSave}
                    multiline
                    minRows="4"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    label={t('assessment.details.scope')}
                    sx={{ backgroundColor: 'white' }}
                  />
                ) : (
                  <Stack spacing={1}>
                    <Typography>{t('assessment.details.scope')}</Typography>
                    <Typography variant="body2">{assessmentDetails.scope}</Typography>
                  </Stack>
                )}
                {editMode ? (
                  <TextField
                    id="criteria"
                    name="criteria"
                    value={textFields.criteria}
                    onChange={handleTextChange}
                    onBlur={handleSave}
                    multiline
                    minRows="4"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    label={t('assessment.details.criteria')}
                    sx={{ backgroundColor: 'white' }}
                  />
                ) : (
                  <Stack spacing={1}>
                    <Typography>{t('assessment.details.criteria')}</Typography>
                    <Typography variant="body2">{assessmentDetails.criteria}</Typography>
                  </Stack>
                )}
                {editMode ? (
                  <TextField
                    id="objectives"
                    name="objectives"
                    value={textFields.objectives}
                    onChange={handleTextChange}
                    onBlur={handleSave}
                    multiline
                    minRows="4"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    label={t('assessment.details.objectives')}
                    sx={{ backgroundColor: 'white' }}
                  />
                ) : (
                  <Stack spacing={1}>
                    <Typography>{t('assessment.details.objectives')}</Typography>
                    <Typography variant="body2">{assessmentDetails.objectives}</Typography>
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Container>
        )}
      </Paper>
    </Container>
  );
}

export default ReviewDetails;
